import { ellipse, type Ctx } from './paint';
import { noise2 } from './noise';
import { wind } from './nature';

export type ParticleKind = 'dust' | 'steam' | 'mote' | 'petal' | 'leaf' | 'spark' | 'heart' | 'note' | 'drop' | 'firefly';

interface Particle {
  kind: ParticleKind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  color: string;
  spin: number;
}

interface Floater {
  text: string;
  color: string;
  x: number;
  y: number;
  age: number;
}

const MAX_PARTICLES = 320;
const MAX_DECALS = 260;

interface Decal {
  x: number;
  y: number;
  rot: number;
  color: string;
  age: number;
}

/** Particle system + floating texts, all in world coordinates. */
export class Effects {
  private ps: Particle[] = [];
  private floaters: Floater[] = [];
  private floatQueue = 0;
  private decals: Decal[] = [];

  spawn(kind: ParticleKind, x: number, y: number, o: Partial<Particle> = {}): void {
    if (this.ps.length >= MAX_PARTICLES) return;
    const r = Math.random;
    const base: Record<ParticleKind, Omit<Particle, 'kind' | 'x' | 'y'>> = {
      dust: { vx: (r() - 0.5) * 12, vy: -6 - r() * 6, life: 0.45, max: 0.45, size: 2, color: 'rgba(225,205,180,0.6)', spin: 0 },
      steam: { vx: (r() - 0.5) * 6, vy: -14 - r() * 10, life: 1.6, max: 1.6, size: 2.2, color: 'rgba(255,252,245,0.5)', spin: 0 },
      mote: { vx: (r() - 0.5) * 4, vy: -3 - r() * 4, life: 5, max: 5, size: 1, color: 'rgba(255,245,210,0.8)', spin: 0 },
      petal: { vx: 10 + r() * 14, vy: 6 + r() * 6, life: 4 + r() * 5, max: 9, size: 1.7, color: ['#ffb7cf', '#ffd3e2', '#ff9fc0', '#fff0f5'][Math.floor(r() * 4)], spin: r() * 6 },
      leaf: { vx: 20 + r() * 10, vy: 4 + r() * 6, life: 3 + r() * 4, max: 7, size: 1.8, color: ['#4f9a4a', '#8cc45a', '#c9b24a'][Math.floor(r() * 3)], spin: r() * 6 },
      spark: { vx: (r() - 0.5) * 90, vy: -40 - r() * 70, life: 0.9, max: 0.9, size: 2, color: ['#ffe066', '#fff6c0', '#ffb347'][Math.floor(r() * 3)], spin: 0 },
      heart: { vx: (r() - 0.5) * 30, vy: -25 - r() * 20, life: 1.4, max: 1.4, size: 6, color: '#ff6f9c', spin: 0 },
      note: { vx: 8 + r() * 8, vy: -18 - r() * 6, life: 1.4, max: 1.4, size: 8, color: '#ffd34e', spin: 0 },
      drop: { vx: (r() - 0.5) * 40, vy: -30 - r() * 30, life: 0.7, max: 0.7, size: 2, color: '#7fc8f0', spin: 0 },
      firefly: { vx: (r() - 0.5) * 8, vy: (r() - 0.5) * 8, life: 6, max: 6, size: 1.5, color: '#f6ff9a', spin: r() * 6 },
    };
    this.ps.push({ kind, x, y, ...base[kind], ...o });
  }

  burst(kind: ParticleKind, x: number, y: number, n: number): void {
    for (let i = 0; i < n; i++) this.spawn(kind, x + (Math.random() - 0.5) * 10, y + (Math.random() - 0.5) * 6);
  }

  float(text: string, color: string, x: number, y: number): void {
    // Stagger simultaneous floaters so they don't overlap.
    this.floaters.push({ text, color, x, y: y - this.floatQueue * 12, age: -this.floatQueue * 0.12 });
    this.floatQueue++;
  }

  update(dt: number): void {
    this.floatQueue = 0;
    for (let i = this.ps.length - 1; i >= 0; i--) {
      const p = this.ps[i];
      p.life -= dt;
      if (p.life <= 0) {
        this.ps.splice(i, 1);
        continue;
      }
      if (p.kind === 'spark' || p.kind === 'drop') p.vy += 160 * dt;
      if (p.kind === 'petal' || p.kind === 'leaf') {
        // Carried by the wind with a little turbulence, fluttering as it tumbles.
        const w = wind.at(p.x, p.y);
        const target = 14 + w * 60;
        p.vx += (target - p.vx) * Math.min(1, dt * 1.6) + (noise2(p.x * 0.03, p.y * 0.03 + wind.time() * 0.5, 7) - 0.5) * 90 * dt;
        p.spin += dt * (2.5 + w * 4);
        p.vy = (p.kind === 'leaf' ? 10 : 7) + Math.sin(p.spin * 1.3) * 9 - w * 3;
        if (p.life - dt <= 0 && this.decals.length < MAX_DECALS) {
          this.decals.push({ x: p.x, y: p.y, rot: p.spin, color: p.color, age: 0 });
        }
      }
      if (p.kind === 'firefly') {
        p.spin += dt;
        p.vx = Math.cos(p.spin * 1.7) * 6;
        p.vy = Math.sin(p.spin * 1.3) * 5;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }
    for (let i = this.decals.length - 1; i >= 0; i--) {
      const d = this.decals[i];
      d.age += dt;
      // Fallen petals skitter a little in gusts, then fade.
      if (d.age < 25 && wind.at(d.x, d.y) > 0.95) d.x += dt * 6;
      if (d.age > 40) this.decals.splice(i, 1);
    }
    for (let i = this.floaters.length - 1; i >= 0; i--) {
      const f = this.floaters[i];
      f.age += dt;
      if (f.age > 1.5) this.floaters.splice(i, 1);
    }
  }

  draw(c: Ctx, top: number, bottom: number, t: number): void {
    for (const p of this.ps) {
      if (p.y < top - 20 || p.y > bottom + 20) continue;
      const a = Math.min(1, p.life / p.max * 1.5);
      c.globalAlpha = a;
      switch (p.kind) {
        case 'heart':
        case 'note':
          c.fillStyle = p.color;
          c.font = `${p.size + 2}px sans-serif`;
          c.textAlign = 'center';
          c.fillText(p.kind === 'heart' ? '♥' : '♪', p.x, p.y);
          break;
        case 'petal':
        case 'leaf': {
          c.save();
          c.translate(p.x, p.y);
          c.rotate(p.spin * 0.6);
          c.scale(1, Math.max(0.2, Math.abs(Math.cos(p.spin))));
          c.fillStyle = p.color;
          c.beginPath();
          if (p.kind === 'petal') {
            c.moveTo(0, -p.size);
            c.quadraticCurveTo(p.size * 1.1, 0, 0, p.size);
            c.quadraticCurveTo(-p.size * 1.1, 0, 0, -p.size);
          } else c.ellipse(0, 0, p.size * 1.2, p.size * 0.6, 0, 0, Math.PI * 2);
          c.fill();
          c.fillStyle = 'rgba(255,255,255,0.45)';
          c.fillRect(-0.25, -p.size * 0.6, 0.5, p.size * 0.8);
          c.restore();
          break;
        }
        case 'firefly': {
          const glow = 0.5 + 0.5 * Math.sin(t * 4 + p.spin * 3);
          c.globalAlpha = a * glow;
          ellipse(c, 'rgba(240,255,140,0.25)', p.x, p.y, 4, 4);
          ellipse(c, p.color, p.x, p.y, p.size, p.size);
          break;
        }
        case 'steam':
          ellipse(c, p.color, p.x, p.y, p.size * (2 - p.life / p.max), p.size * (2 - p.life / p.max));
          break;
        default:
          c.fillStyle = p.color;
          c.fillRect(Math.round(p.x), Math.round(p.y), p.size, p.size);
      }
    }
    c.globalAlpha = 1;
  }

  /** Fallen petals lying on the ground (drawn under everything that stands). */
  drawDecals(c: Ctx, x0: number, x1: number, y0: number, y1: number): void {
    for (const d of this.decals) {
      if (d.x < x0 || d.x > x1 || d.y < y0 || d.y > y1) continue;
      c.globalAlpha = d.age > 30 ? Math.max(0, 1 - (d.age - 30) / 10) : 0.9;
      c.save();
      c.translate(d.x, d.y);
      c.rotate(d.rot);
      c.fillStyle = d.color;
      c.beginPath();
      c.ellipse(0, 0, 1.4, 0.8, 0, 0, Math.PI * 2);
      c.fill();
      c.restore();
    }
    c.globalAlpha = 1;
  }

  /** Floating reward texts are drawn last, above lighting. */
  drawFloaters(c: Ctx): void {
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    c.font = '800 11px system-ui, "PingFang TC", "Noto Sans TC", sans-serif';
    for (const f of this.floaters) {
      if (f.age < 0) continue;
      const k = f.age / 1.5;
      const y = f.y - 26 - easeOut(Math.min(1, k * 1.4)) * 26;
      c.globalAlpha = k < 0.7 ? 1 : 1 - (k - 0.7) / 0.3;
      c.lineWidth = 3;
      c.strokeStyle = 'rgba(30,16,8,0.85)';
      c.strokeText(f.text, f.x, y);
      c.fillStyle = f.color;
      c.fillText(f.text, f.x, y);
    }
    c.globalAlpha = 1;
  }

  clear(): void {
    this.decals = [];
    this.ps = [];
    this.floaters = [];
  }
}

export function easeOut(t: number): number {
  return 1 - (1 - t) * (1 - t);
}
